import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { Box, Button, Grid, Typography, Modal, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Checkbox, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { useTQuery } from '../../../utils/useTQuery';

export default function AssessmentModal({ open, handleClose, specialityID }) {
  const { controlBy, controlUser } = useSelector(state => state.user);
  const { userDetails } = useSelector((state) => state.user);
  const [assessmentData, setAssessmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

const { data: assessment, refetch: fetchAgain, isLoading: isApiLoading } = useTQuery(
    ["assessment", specialityID],  // 👈 add prefix
    `/fetchSpecialityAssessment?${new URLSearchParams({ 
        specialityID: specialityID, 
        clinicianID: controlBy === "admin" ? controlUser : userDetails?.ID 
    }).toString()}`,
    'get',
    { enabled: !!specialityID }  // 👈 only fetch when specialityID exists
);

  useEffect(() => {
    if (open && specialityID) {
      setIsLoading(true);
      fetchAgain().finally(() => {
        setIsLoading(false);
      });
    }
  }, [open, specialityID, fetchAgain]);

  useEffect(() => {
    if (assessment?.OUTPUT) {
      setAssessmentData(assessment.OUTPUT);
    }
  }, [assessment]);

  const handleDownloadPDF = () => {
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      const styles = `
        <style>
          @page { 
            margin: 0; 
            size: A4;
          }
          @media print {
            @page { 
              margin: 0; 
              size: A4;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            body { 
              margin: 15mm;
              padding: 0;
            }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            @top-left { content: ""; }
            @top-center { content: ""; }
            @top-right { content: ""; }
            @bottom-left { content: ""; }
            @bottom-center { content: ""; }
            @bottom-right { content: ""; }
          }
          body { font-family: Arial, sans-serif; margin: 20px; }
          .assessment-title { font-size: 18px; font-weight: bold; margin-bottom: 20px; }
          .assessment-section { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; }
          .section-title { font-size: 16px; font-weight: bold; margin-bottom: 15px; }
          .assessment-name { font-size: 14px; font-weight: 600; margin-bottom: 10px; }
          .assessment-meta { font-size: 12px; color: #666; margin-bottom: 20px; }
          .assessment-item { margin-bottom: 15px; }
          .item-text { font-size: 14px; margin-bottom: 10px; }
          .checkbox-grid { display: flex; gap: 10px; align-items: center; }
          .checkbox { width: 20px; height: 20px; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; }
          .checkbox.checked { background-color: #6ebe44; color: white; }
          .checkbox.disabled { background-color: #f5f5f5; color: #999; }
          .correct-label { color: #6ebe44; font-size: 12px; margin-left: 10px; }
          hr { margin: 20px 0; }
          .assessment-section { page-break-inside: avoid; }
        </style>
      `;
      
      let pdfContent = `
        <html>
          <head>
            <title>${assessmentData?.SPECIALTY_NAME || 'Assessment'} PDF</title>
            ${styles}
          </head>
          <body>
            <div class="assessment-title">${assessmentData?.SPECIALTY_NAME || 'Assessment'} Assessment</div>
            <hr>
      `;
      
      // Generate assessment content
      assessmentData?.adminScheduleAssessment?.forEach((assessmentItem, index) => {
        pdfContent += `
          <div class="assessment-section">
            <div class="section-title">${assessmentItem.assessmentTitle || ''}</div>
            <div class="assessment-name">${assessmentItem.assessmentName || ''}</div>
            <div class="assessment-meta">
              Version: ${assessmentItem.version || 'N/A'} | 
              Passing Criteria: ${assessmentItem.passingCriteria || 'N/A'} | 
              Re-attempts: ${assessmentItem.reattempts || 'N/A'}
            </div>
        `;
        
        // Add assessment options
        assessmentItem.assessmentOptions?.forEach((option, optionIndex) => {
          pdfContent += `
            <div class="assessment-item">
              <div class="item-text">${option.assessmentOption || ''}</div>
              <div class="checkbox-grid">
                <div class="checkbox ${option.answer ? 'checked' : 'disabled'}">
                  ${option.answer ? '✓' : ''}
                </div>
                <span class="item-text">
                  ${option.assessmentOption || ''}
                  ${option.answer ? '<span class="correct-label">(Correct Answer)</span>' : ''}
                </span>
              </div>
            </div>
          `;
        });
        
        pdfContent += `</div>`;
      });
      
      pdfContent += `
          </body>
        </html>
      `;
      
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Try to remove headers and footers
      printWindow.onload = function() {
        setTimeout(() => {
          printWindow.document.title = ''; // Remove title
          printWindow.print();
          
          // Close after printing
          printWindow.onafterprint = function() {
            printWindow.close();
          };
          
          // Fallback close
          setTimeout(() => {
            printWindow.close();
          }, 1000);
        }, 500);
      };
    }
  };
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxWidth: 900,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="assessment-modal-title"
      aria-describedby="assessment-modal-description"
    >
      <Box sx={modalStyle}>
        <IconButton
          onClick={handleClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'rgba(0,0,0,0.54)',
            '&:hover': {
              color: 'rgba(0,0,0,0.87)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        {(isLoading || isApiLoading) ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress sx={{ color: '#6ebe44' }} />
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography
                id="assessment-modal-title"
                sx={{
                  fontSize: "18px",
                  color: "rgba(0,0,0,0.87)",
                  fontWeight: 700
                }}
              >
                {assessmentData?.SPECIALTY_NAME} Assessment
              </Typography>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadPDF}
                sx={{
                  border: "1px solid #6ebe44",
                  color: "#6ebe44",
                  '&:hover': {
                    backgroundColor: '#6ebe44',
                    color: 'white'
                  }
                }}
              >
                Download PDF
              </Button>
            </Box>
            
            {assessmentData?.adminScheduleAssessment?.length > 0 ? (
              assessmentData.adminScheduleAssessment.map((assessmentItem, index) => (
                <Accordion key={index} sx={{ mb: 2 }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`assessment-panel-${index}-content`}
                    id={`assessment-panel-${index}-header`}
                    sx={{ py: 2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: '16px',
                        color: 'rgba(0,0,0,0.87)',
                        fontWeight: 700
                      }}
                    >
                      {assessmentItem.assessmentTitle}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'rgba(0,0,0,0.87)',
                        fontWeight: 600,
                        mb: 2
                      }}
                    >
                      {assessmentItem.assessmentName}
                    </Typography>
                    
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'rgba(0,0,0,0.6)',
                        mb: 2
                      }}
                    >
                      Version: {assessmentItem.version} | 
                      Passing Criteria: {assessmentItem.passingCriteria} | 
                      Re-attempts: {assessmentItem.reattempts}
                    </Typography>

                    <Grid container spacing={2}>
                      {assessmentItem.assessmentOptions?.map((option, optionIndex) => (
                        <Grid item xs={12} key={optionIndex}>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Checkbox
                              checked={option.answer}
                              disabled
                              sx={{
                                '&.Mui-checked': {
                                  color: option.answer ? '#6ebe44 !important' : 'default'
                                },
                                '&:focus': {
                                  outline: 'none'
                                }
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: '14px',
                                color: 'rgba(0,0,0,0.87)',
                                fontWeight: option.answer ? 600 : 400
                              }}
                            >
                              {option.assessmentOption}
                              {option.answer && (
                                <Box component="span" sx={{ ml: 1, color: '#6ebe44', fontSize: '12px' }}>
                                  (Correct Answer)
                                </Box>
                              )}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))
            ) : (
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "rgba(0,0,0,0.87)",
                  fontWeight: 400,
                  display: "flex",
                  my: 4,
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                No Assessment Found for this Speciality.
              </Typography>
            )}

            <Box display="flex" justifyContent="end" sx={{ mt: 4 }}>
              <Button 
                variant="outlined" 
                onClick={handleClose} 
                sx={{ border: "1px solid lightgrey", color: "#6ebe44" }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}
